/**
 * EndpointSetModal
 *
 * Generic modal for viewing and managing a set of endpoints (search, multi-select,
 * remove, clear-all). Used by ViewMapSetModal and ViewCollectionSetModal.
 */

import type { ReactElement } from 'react'
import type { CaseEndpoint } from '../../types/case-container'
import {
  EndpointSetHeader,
  EndpointSetSearchBar,
  EndpointSetEmpty,
  EndpointSetFooter,
  EndpointRow,
  type EndpointSetCopy,
  type EndpointSetVisuals,
} from './endpoint-set-modal.parts'
import { useEndpointSetModal } from './use-endpoint-set-modal'

export interface EndpointSetModalProps {
  endpointIds: Set<string>
  endpoints: CaseEndpoint[]
  visuals: EndpointSetVisuals
  copy: EndpointSetCopy
  onClose: () => void
  onRemove: (endpointId: string) => void
  onRemoveMany: (endpointIds: string[]) => void
  onClear: () => void
}

interface EndpointListProps {
  filteredEndpoints: CaseEndpoint[]
  selectedIds: Set<string>
  onToggle: (id: string) => void
  onRemove: (id: string) => void
  removeTooltipKey: string
}

function EndpointList({
  filteredEndpoints,
  selectedIds,
  onToggle,
  onRemove,
  removeTooltipKey,
}: EndpointListProps): ReactElement {
  return (
    <div className="space-y-2">
      {filteredEndpoints.map((ep) => (
        <EndpointRow
          key={ep.id}
          endpoint={ep}
          isSelected={selectedIds.has(ep.id)}
          onToggle={onToggle}
          onRemove={onRemove}
          removeTooltipKey={removeTooltipKey}
        />
      ))}
    </div>
  )
}

export function EndpointSetModal({
  endpointIds,
  endpoints,
  visuals,
  copy,
  onClose,
  onRemove,
  onRemoveMany,
  onClear,
}: EndpointSetModalProps): ReactElement {
  const modal = useEndpointSetModal({ endpointIds, endpoints, onRemoveMany, onClear })

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col rounded-xl bg-white shadow-xl dark:bg-slate-900">
        <EndpointSetHeader
          visuals={visuals}
          copy={copy}
          count={endpointIds.size}
          onClose={onClose}
        />
        <EndpointSetSearchBar
          searchTerm={modal.searchTerm}
          onSearchChange={modal.setSearchTerm}
          selectedCount={modal.selectedIds.size}
          hasFilteredItems={modal.filteredEndpoints.length > 0}
          onSelectAll={modal.selectAll}
          onDeselectAll={modal.deselectAll}
          onRemoveSelected={modal.handleRemoveSelected}
          selectAllKey={copy.selectAllKey}
        />
        <div className="flex-1 overflow-auto px-6 py-3">
          {modal.filteredEndpoints.length === 0 ? (
            <EndpointSetEmpty visuals={visuals} copy={copy} searchTerm={modal.searchTerm} />
          ) : (
            <EndpointList
              filteredEndpoints={modal.filteredEndpoints}
              selectedIds={modal.selectedIds}
              onToggle={modal.toggleSelect}
              onRemove={onRemove}
              removeTooltipKey={copy.removeTooltipKey}
            />
          )}
        </div>
        <EndpointSetFooter
          copy={copy}
          filteredCount={modal.filteredEndpoints.length}
          totalCount={endpointIds.size}
          searchTerm={modal.searchTerm}
          showClearConfirm={modal.showClearConfirm}
          onShowClearConfirm={modal.onShowClearConfirm}
          onConfirmClear={modal.handleConfirmClear}
          onCancelClear={modal.onCancelClear}
          onClose={onClose}
        />
      </div>
    </div>
  )
}
